/*
 * Picturepops — Google Photos, through Google's Photos Picker API.
 *
 * ONE WINDOW, ONE CLICK (2026-09-29, Daniel: "I did it in other places, like
 * with Mixtiles"). The click opens our own window straight at Google's sign-in.
 * Google sends it back to /?view=google-photos (templates/index.google-photos
 * .liquid), which starts the picker session and carries on to the picker in
 * that same window. The builder never loads Google's script at all.
 *
 *   ⛔ WHY THE TOKEN COMES BACK OVER A BroadcastChannel, NOT window.opener.
 *   accounts.google.com and photos.google.com send Cross-Origin-Opener-Policy,
 *   which moves the window into its own browsing-context group: from the moment
 *   it lands on Google, neither side can reach the other — not even after it
 *   returns to our own origin. A same-origin BroadcastChannel still connects
 *   them. The same header is why popup.closed reads TRUE while the customer is
 *   still choosing; the first rewrite trusted it, deleted the session mid-pick,
 *   and Google answered Done with "Couldn't add photos". Nothing here reads it.
 *
 *   ⛔ WE CANNOT SEE THE WINDOW, so nothing waits on it. A pick polls Google
 *   until Google's own timeout, and a new pick supersedes an old one — which
 *   is also what keeps the button usable behind a window the customer shut.
 *
 * What the privacy notice (snippets/privacy-notice.liquid, sections 4 and 7)
 * says about this, and which must stay true: ONE read-only scope; the token
 * lives in memory only — this module's, and the callback page's for the moment
 * it runs — never in storage, a cookie, or a request to our own servers (it
 * rides in the URL fragment, which browsers never send to a server); the only
 * requests go to Google.
 */
(function () {
  'use strict';
  window.PP = window.PP || {};

  var AUTH = 'https://accounts.google.com/o/oauth2/v2/auth';
  var API = 'https://photospicker.googleapis.com/v1';
  var SCOPE = 'https://www.googleapis.com/auth/photospicker.mediaitems.readonly';
  var CHANNEL = 'pp-gphotos';
  // Registered in Google Cloud Console as an authorised redirect URI, exactly.
  var CALLBACK_PATH = '/?view=google-photos';

  // Google's own pollingConfig overrides both. These are the fallbacks.
  var POLL_MS = 2000;
  var POLL_TIMEOUT_MS = 10 * 60 * 1000;
  // Sign-in has no pollingConfig; this is how long a click waits for it.
  var AUTH_TIMEOUT_MS = 10 * 60 * 1000;

  var _accessToken = null;
  var _expiresAt = 0;
  var _current = 0;          // the pick in flight; a new one supersedes it

  function err(code) { var e = new Error(code); e.code = code; return e; }

  function isConfigured() {
    return !!(window.PP && window.PP._gphotos_client_id) && 'BroadcastChannel' in window;
  }

  function isConnected() {
    // A minute's margin: a token that expires mid-pick fails in the worst place.
    return !!_accessToken && Date.now() < _expiresAt - 60 * 1000;
  }

  function apiFetch(url, options) {
    options = options || {};
    options.headers = Object.assign({}, options.headers || {}, {
      Authorization: 'Bearer ' + _accessToken,
    });
    return fetch(url, options).then(function (r) {
      // Forget a dead token; the next click signs in again in the same window.
      if (r.status === 401) { _accessToken = null; throw err('reconnect'); }
      return r;
    });
  }

  function json(r, what) {
    if (!r.ok) throw err(what + '_' + r.status);
    return r.json();
  }

  // "5s", "1800s" → ms. Google's Duration strings.
  function durationMs(d, fallback) {
    var n = parseFloat(String(d || ''));
    return isFinite(n) && n > 0 ? n * 1000 : fallback;
  }

  // Waits `ms`, or less: autoclose shuts the picker on Done and focus comes
  // back to the builder, which is the moment worth looking straight away.
  function waitOrFocus(ms) {
    return new Promise(function (resolve) {
      var t = setTimeout(done, ms);
      function done() { clearTimeout(t); window.removeEventListener('focus', done); resolve(); }
      window.addEventListener('focus', done);
    });
  }

  function nonce() {
    var a = new Uint8Array(12);
    (window.crypto || window.msCrypto).getRandomValues(a);
    return Array.prototype.map.call(a, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
  }

  // Resolves { session } once the callback page has signed in and started a
  // session. The state carries the count the picker should allow, because the
  // callback page creates the session and has no other way to know it.
  function signIn(popup, maxCount, ticket) {
    var state = nonce() + '.' + maxCount;
    var url = AUTH +
      '?client_id=' + encodeURIComponent(window.PP._gphotos_client_id) +
      '&redirect_uri=' + encodeURIComponent(location.origin + CALLBACK_PATH) +
      '&response_type=token' +
      '&scope=' + encodeURIComponent(SCOPE) +
      '&include_granted_scopes=true' +
      '&state=' + encodeURIComponent(state);
    popup.location.href = url;

    return new Promise(function (resolve, reject) {
      var bc = new BroadcastChannel(CHANNEL);
      var timer = setTimeout(function () { finish(err('timed_out')); }, AUTH_TIMEOUT_MS);
      var sup = setInterval(function () { if (ticket !== _current) finish(err('superseded')); }, 1000);
      function finish(e, v) {
        clearTimeout(timer); clearInterval(sup); bc.close();
        if (e) reject(e); else resolve(v);
      }
      bc.onmessage = function (ev) {
        var m = ev.data || {};
        // Only the answer to THIS sign-in. Anything else on the channel —
        // an older window finishing late, another tab — is not ours.
        if (m.type !== 'pp-gphotos' || m.state !== state) return;
        if (m.error) return finish(err(m.error));
        _accessToken = m.token;
        _expiresAt = Date.now() + (Number(m.expiresIn) || 3600) * 1000;
        finish(null, { session: m.session });
      };
    });
  }

  function newSession(popup, maxCount) {
    return apiFetch(API + '/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pickingConfig: { maxItemCount: String(maxCount) } }),
    })
      .then(function (r) { return json(r, 'session'); })
      .then(function (session) {
        // Still about:blank and ours, so this navigation works. /autoclose:
        // Google shuts the window itself when the customer taps Done.
        popup.location.href = session.pickerUri.replace(/\/$/, '') + '/autoclose';
        return { session: session };
      });
  }

  function pollSession(session, ticket) {
    var pc = session.pollingConfig || {};
    var every = durationMs(pc.pollInterval, POLL_MS);
    var deadline = Date.now() + durationMs(pc.timeoutIn, POLL_TIMEOUT_MS);
    function tick() {
      if (ticket !== _current) return Promise.reject(err('superseded'));
      return apiFetch(API + '/sessions/' + session.id)
        .then(function (r) { return json(r, 'poll'); })
        .then(function (s) {
          if (s.mediaItemsSet) return s;
          if (Date.now() > deadline) throw err('timed_out');
          return waitOrFocus(every).then(tick);
        });
    }
    return tick();
  }

  function listPhotos(sessionId, maxCount) {
    var items = [], skipped = 0;
    function page(token) {
      var url = API + '/mediaItems?pageSize=100&sessionId=' + encodeURIComponent(sessionId) +
        (token ? '&pageToken=' + encodeURIComponent(token) : '');
      return apiFetch(url).then(function (r) { return json(r, 'list'); }).then(function (res) {
        (res.mediaItems || []).forEach(function (it) {
          // The picker cannot be limited to photos; a video's baseUrl would
          // hand back a still frame, which is not what the customer chose.
          var f = it.mediaFile;
          if (it.type === 'VIDEO' || !f || !f.baseUrl) { skipped++; return; }
          var md = f.mediaFileMetadata || {};
          items.push({
            id: it.id,
            baseUrl: f.baseUrl,
            name: (f.filename || 'google-photo').replace(/\.[^.]+$/, '') + '.jpg',
            // The ORIGINAL's size. The builder sizes its placeholder from it and
            // keeps it as srcW/srcH, the resolution check's only honest input.
            width: Number(md.width) || 0,
            height: Number(md.height) || 0,
          });
        });
        return res.nextPageToken ? page(res.nextPageToken) : null;
      });
    }
    return page(null).then(function () {
      return { items: items.slice(0, maxCount), skipped: skipped };
    });
  }

  function deleteSession(id) {
    if (id && _accessToken) apiFetch(API + '/sessions/' + id, { method: 'DELETE' }).catch(function () {});
  }

  // Must be called synchronously from the click: the window is opened before
  // anything is awaited, and that is the one window the click can buy.
  // Resolves { items, skipped, done }. The builder downloads the items with
  // download() and then calls done(), which deletes the session — Google asks
  // for that once the items are fetched, and it ends our access to the set.
  // Never before the downloads: that is what broke Done the first time.
  function pickPhotos(opts) {
    var maxCount = Math.max(1, (opts && opts.maxCount) || 4);
    if (!isConfigured()) return Promise.reject(err('not_configured'));

    var popup = window.open('about:blank', 'pp-gphotos', 'width=900,height=720');
    if (!popup) return Promise.reject(err('popup_blocked'));

    var ticket = ++_current;
    var sessionId = null;
    var started = isConnected() ? newSession(popup, maxCount) : signIn(popup, maxCount, ticket);
    return started
      .then(function (r) { sessionId = r.session.id; return pollSession(r.session, ticket); })
      .then(function () { return listPhotos(sessionId, maxCount); })
      .then(function (res) {
        var id = sessionId;
        res.done = function () { deleteSession(id); };
        return res;
      }, function (e) {
        deleteSession(sessionId);
        throw e;
      });
  }

  // One picked photo at `size` px on its long side, as a File, so the builder's
  // own importer takes it like any other.
  function download(item, size) {
    var s = Math.max(1, Math.round(size || 1600));
    return apiFetch(item.baseUrl + '=w' + s + '-h' + s).then(function (r) {
      if (!r.ok) throw err('download_' + r.status);
      return r.blob();
    }).then(function (blob) {
      return new File([blob], item.name, { type: blob.type || 'image/jpeg' });
    });
  }

  window.PP.gphotos = {
    isConfigured: isConfigured,
    isConnected: isConnected,
    pickPhotos: pickPhotos,
    download: download,
  };
})();
